import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Medication from '../../Scripts/AddScript/Medication/Medication';
import accountHelpers from '../../../../utils/accountHelpers';
import classes from './orderProduct.module.scss';
import Button from '../../../_common/Form/Buttons/Button';
import DOBPicker from '../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import InputField from '../../../_common/Form/TextFields/InputField';
import api from '../../../../api/api';
import { clearAdjustProduct, getAdjustProductRequest, getProductOrderRequest } from '../../../../store/actions/products';
import Loader from '../../../_common/Loader/Loader';

const orderDateInvoice = [
  {
    name: 'Order Date',
    path: 'orderDate',
  },
  {
    name: 'Invoice #',
    path: 'invoiceNum',
  },
];

const orderVendorMemo = [
  {
    name: 'Vendor',
    path: 'vendor',
  },
  {
    name: 'Memo',
    path: 'memo',
  },
];

const adjustDateMemo = [
  {
    name: 'Order Date',
    path: 'orderDate',
  },
  {
    name: 'Memo',
    path: 'memo',
  },
];

const orderRequestData = {
  invoiceNum: '',
  orderDate: moment(new Date()).format('YYYY-MM-DD'),
  orderId: Math.floor(Math.random() * 9000000) + 1000000,
  vendor: '',
  memo: '',
  writtenBy: accountHelpers?.getUser()?.username,
};

const adjustRequestData = {
  orderDate: moment(new Date()).format('YYYY-MM-DD'),
  adjustId: Math.floor(Math.random() * 9000000) + 1000000,
  memo: '',
  writtenBy: accountHelpers?.getUser()?.username,
};

const calcCost = (cost, qty = 0, packageSize = 0) => {
  const currentCost = (cost / qty) * packageSize;

  return Math.round((Number.isFinite(currentCost) ? currentCost : 0) * 100) / 100;
};

const OrderAdjustProduct = ({ adjust, edit }) => {
  const { adjustId } = useParams();

  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { adjustProduct } = useSelector((store) => store.products);

  const [requestData, setRequestData] = useState({});

  const {
    orderDate, vendor, invoiceNum, memo,
  } = requestData;

  const [products, setProducts] = useState([]);
  const [dateError, setDateError] = useState('');
  const [errors, setErrors] = useState([]);
  const [medicationError, setMedicationError] = useState('');

  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const medicationPath = ['expiration', 'lot', 'cost'];

  useEffect(() => {
    if (edit) {
      if (adjust) {
        dispatch(getAdjustProductRequest({ adjustId }));
      } else {
        dispatch(getProductOrderRequest({ orderId: adjustId }));
      }

      return () => dispatch(clearAdjustProduct());
    }

    setLoading(false);
    setRequestData(adjust ? adjustRequestData : orderRequestData);
  }, [adjustId]);

  useEffect(() => {
    (async () => {
      if (adjustProduct.length && edit) {
        const { data } = adjust
          ? await api.getAdjustProduct({ batchId: adjustProduct[0].adjustId })
          : await api.getProductOrder({ batchId: adjustProduct[0].orderId });

        for (const p of data.response) {
          const { data: { data: { response } } } = await api.getProducts({ productId: p.ProductId });

          const newData = { ...p };

          if (!adjust) {
            const { cost, packageSize } = response[0];

            newData.cost = updateCost(p.qtyChange, cost, packageSize);
          }

          setProducts((prev) => [...prev, { ...response[0], data: newData }]);
        }

        setRequestData((prev) => {
          const newRequestData = {
            ...prev,
            orderDate: adjustProduct[0].orderDate,
            memo: adjustProduct[0].memo,
            adjustId: adjustProduct[0].adjustId,
            writtenBy: adjustProduct[0].writtenBy || accountHelpers?.getUser()?.username,
          };

          if (!adjust) {
            delete newRequestData.adjustId;

            newRequestData.orderId = adjustProduct[0].orderId;
            newRequestData.vendor = adjustProduct[0].vendor;
            newRequestData.invoiceNum = adjustProduct[0].invoiceNum;
          }

          return newRequestData;
        });

        setLoading(false);
      }
    })();
  }, [adjustProduct]);

  const setProduct = useCallback((path, data, id) => {
    setErrors((prev) => _.compact(prev.map((p) => {
      if (p.id === id) {
        delete p[path];

        if (path === 'quantity') delete p.cost;

        if (Object.keys(p).length === 1) return null;

        return p;
      }

      return p;
    })));

    if (path === 'productsSelected') {
      setMedicationError('');

      setProducts((prev) => {
        let newSelectedProducts = [];

        const newData = {
          expiration: '',
          lot: '',
          ProductId: data.id,
          qtyChange: '',
        };

        if (!adjust) {
          newData.cost = 0;
        }

        newSelectedProducts = [...prev, { ...data, data: newData }];

        if (prev.find((p) => p.id === data.id)) {
          newSelectedProducts = prev.filter((p) => p.id !== data.id);
        }

        return newSelectedProducts;
      });
    } else if (path === 'quantity') {
      setProducts((prev) => prev.map((p) => {
        if (p.id === id) {
          const newData = {
            ...p.data,
            qtyChange: data,
          };

          if (!adjust) {
            newData.cost = updateCost(data, p.cost, p.packageSize);
          }

          return {
            ...p,
            data: newData,
          };
        }

        return p;
      }));
    } else if (medicationPath.includes(path)) {
      setProducts((prev) => prev.map((p) => (p.id === id
        ? {
          ...p,
          data: {
            ...p.data,
            [path]: data,
          },
        }
        : p)));
    } else {
      setRequestData((prev) => ({ ...prev, [path]: data }));
    }
  }, [products]);

  const updateCost = (qty, price, packageSize) => {
    const currentCost = (+price / +packageSize) * +qty;

    return Math.round((Number.isFinite(currentCost) ? currentCost : 0) * 100) / 100;
  };

  const onSave = async () => {
    let hasError = false;

    if (orderDate && orderDate.length !== 10) {
      setDateError('Invalid date');
      hasError = true;
    }

    if (!products.length) {
      setMedicationError('Please select medication');

      hasError = true;
    }

    const notRequiredFields = ['memo', 'invoiceNum', 'vendor', 'qtyChange', 'lot', 'expiration'];

    products.forEach((p) => {
      let error = {};

      _.forEach(p.data, (v, key) => {
        if (!v && key !== 'writtenBy') {
          if (!notRequiredFields.includes(key)) {
            error[key] = 'Field is required';
          }
        } else if (key === 'expiration' && v.length !== 10) {
          error.expiration = 'Invalid date';
        }
      });

      if (!_.isEmpty(error)) {
        const newError = { ...error };
        setErrors((prev) => _.uniqBy([{ ...newError, id: p.id }, ...prev], 'id'));
        hasError = true;
      }

      error = {};
    });

    if (!hasError) {
      try {
        setSaveLoading(true);

        if (!edit) {
          for (const p of products) {
            await api.updateProduct({
              id: p.id,
              name: p.name,
              quantity: +p.data.qtyChange,
              cost: p.data.cost ? +p.data.cost : undefined,
              order: true,
            });

            if (adjust) {
              await api.addAdjustProduct({ ...requestData, ...p?.data });
            } else {
              await api.addProductOrder({ ...requestData, ...p?.data });
            }
          }
        } else {
          for (const p of products) {
            const data = {};
            await api.updateProduct({
              id: p.id,
              name: p.name,
              quantity: +p.quantity + +p.data.qtyChange,
              cost: calcCost(p.data.cost, p.data.qtyChange, p.packageSize),
            });

            _.forEach(p.data, (v, key) => {
              if (key !== 'ProductId' && key !== 'adjustId' && key !== 'orderId') {
                data[key] = v;
              }
            });

            if (p.data) {
              const sendData = { ...data, ...requestData, id: adjustId };

              if (adjust) {
                await api.updateAdjustProduct(sendData);
              } else {
                await api.updateProductOrder(sendData);
              }
            }
          }
        }

        setSaveLoading(false);
        navigate('/products');
      } catch (e) {
        console.warn(e);
      }
    } else {
      toast.error('Please fill this fields');
    }
  };

  const dataInput = adjust ? adjustDateMemo : orderDateInvoice;

  return (
    loading ? <Loader className={classes.loader} />
      : (
        <div className={classes.wrapper}>
          <div className={classes.header}>
            <h2>{adjust ? 'Adjust' : 'Order'}</h2>

            <div className={classes.header_btn}>
              {!adjust && !edit && (
                <Button
                  title="Import product"
                  type="button"
                  className={classes.btn_import}
                  downloadButton
                  buttonColor="#5F6C8E"
                  onClick={() => navigate('/products/import')}
                />
              )}

              <Button
                title="Save"
                type="button"
                className={classes.btn_import}
                onClick={onSave}
                loading={saveLoading}
                reverseColor
              />

              <Button
                href="/products"
                title="Cancel"
                type="button"
                className={classes.btn_import}
                clearButton
              />
            </div>
          </div>

          <div className={classes.input}>
            <div>
              {dataInput.map((d) => (
                d.name === 'Order Date'
                  ? (
                    <React.Fragment key={d.name}>
                      <DOBPicker
                        label={d.name}
                        date={orderDate}
                        dateFormat="YYYY-MM-DD"
                        onChange={(date) => setProduct(d.path, date)}
                        autoValidation={false}
                        error={dateError}
                      />
                    </React.Fragment>

                  )
                  : (
                    <React.Fragment key={d.name}>
                      <InputField
                        label={d.name}
                        value={adjust ? memo : invoiceNum}
                        onChange={(value) => setProduct(d.path, value)}
                      />
                    </React.Fragment>
                  )
              ))}

            </div>

            {!adjust && (
            <div>
              {orderVendorMemo.map((o) => (
                <React.Fragment key={o.name}>
                  <InputField
                    label={o.name}
                    value={o.path === 'vendor' ? vendor : memo}
                    onChange={(value) => setProduct(o.path, value)}
                  />
                </React.Fragment>
              ))}
            </div>
            )}
          </div>

          <Medication
            medicationInfo={products}
            setMedication={setProduct}
            adjust={adjust || false}
            product={!adjust}
            errors={errors}
            error={medicationError}
          />
        </div>
      )
  );
};

OrderAdjustProduct.propTypes = {
  adjust: PropTypes.bool,
  edit: PropTypes.bool,
};

OrderAdjustProduct.defaultProps = {
  adjust: false,
  edit: false,
};

export default OrderAdjustProduct;
